import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {
  RotateLeftIcon,
  RotateRightIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon, AlignJustifyIcon, ListOlIcon, ListUlIcon, ImageIcon,
} from '@components/common/Icons';

import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  ElementFormatType,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
} from 'lexical';

import { Button, Modal } from '@components/common';

import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import {
  $isHeadingNode,
} from '@lexical/rich-text';

import { useTranslation } from 'next-i18next';
import { getSelectedNode } from '../utils/getSelectedNode';


import { InsertImageDialog } from './ImagesPlugin';

const LowPriority = 1;


/**
 * @description Renders the lexical editors header with format actions and button
 */
export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');

  const { t } = useTranslation('common');

  // update the toolbar action active state or set the default state
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root'
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
        }
      }
      const node = getSelectedNode(selection);
      const parent = node.getParent();

      setElementFormat(
        $isElementNode(node)
          ? node.getFormatType() || 'left'
          : parent?.getFormatType() || 'left',
      );

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
    }
  }, [editor]);

  const [openBasicModal, setOpenBasicModal] = useState(false);

  // close modal function
  const closeBasicModal = () => {
    setOpenBasicModal(false);
  };


  // update the selected node state, disable and enable the undo and redo buttons
  useEffect(() => mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    }),
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      LowPriority,
    ),
    editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      LowPriority,
    ),
    editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      LowPriority,
    ),
  ), [editor, updateToolbar]);

  // unordered list command
  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  // ordered list command
  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  return (
    <div className="sticky top-headerOffset ml-px rounded-t border-b border-input/50 bg-surface-50 p-2" ref={toolbarRef}>
      {/* undo button */}
      <Button
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        styling="text"
        className="mr-2 p-2"
      >
        <RotateLeftIcon className="size-4 fill-ink" />
      </Button>

      {/* redo button */}
      <Button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        styling="text"
        color="light"
        className="mr-6 p-2"
      >
        <RotateRightIcon className="size-4 fill-ink" />
      </Button>

      {/* bold button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${isBold ? 'border border-ink' : ''}`}
      >
        <BoldIcon className="size-4 fill-ink" />
      </Button>

      {/* italic button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${isItalic ? 'border border-ink' : ''}`}
      >
        <ItalicIcon className="size-4 fill-ink" />
      </Button>

      {/* underline button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        styling="text"
        color="light"
        className={`mr-6 p-2 ${isUnderline ? 'border border-ink' : ''}`}
      >
        <UnderlineIcon className="size-4 fill-ink" />
      </Button>

      {/* align left button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${elementFormat === 'left' ? 'border border-ink' : ''}`}
      >
        <AlignLeftIcon className="size-4 fill-ink" />
      </Button>

      {/* align center button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${elementFormat === 'center' ? 'border border-ink' : ''}`}
      >
        <AlignCenterIcon className="size-4 fill-ink" />
      </Button>

      {/* align right button */}
      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${elementFormat === 'right' ? 'border border-ink' : ''}`}
      >
        <AlignRightIcon className="size-4 fill-ink" />
      </Button>

      <Button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
        }}
        styling="text"
        color="light"
        className={`mr-6 p-2 ${elementFormat === 'justify' ? 'border border-ink' : ''}`}
      >
        <AlignJustifyIcon className="size-4 fill-ink" />
      </Button>

      {/* unordered list button */}
      <Button
        onClick={formatBulletList}
        styling="text"
        color="light"
        className={`mr-2 p-2 ${blockType === 'ul' ? 'border border-ink' : ''}`}
      >
        <ListUlIcon className="size-4 fill-ink" />
      </Button>

      {/* ordered list button */}
      <Button
        onClick={formatNumberedList}
        styling="text"
        color="light"
        className={`mr-6 p-2 ${blockType === 'ol' ? 'border border-ink' : ''}`}
      >
        <ListOlIcon className="size-4 fill-ink" />
      </Button>

      {/* image upload button */}
      <Button
        onClick={() => setOpenBasicModal(true)}
        styling="text"
        color="light"
        className="mr-2 p-2"
      >
        <ImageIcon className="size-4 fill-ink" />
      </Button>

      {/* modal for image upload */}
      <Modal
        isOpen={openBasicModal}
        onClose={closeBasicModal}
        body={(
          <InsertImageDialog
            activeEditor={editor}
            onClose={closeBasicModal}
          />
        )}
        header={t('dropzone.upload_picture')}
        bodyClasses="bg-surface-50"
      />
    </div>
  );
}
